import * as React from 'react';

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'src/utils/shadcn';

const badgeVariants = cva(
    'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive inline-flex w-fit shrink-0 items-center justify-center gap-1.5 overflow-hidden whitespace-nowrap rounded-md border px-2.5 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3',
    {
        variants: {
            variant: {
                default: 'rounded-full',
                tag: 'rounded-sm bg-white',
            },
            theme: {
                default: 'text-gray-950 dark:text-gray-50',
                primary: 'border-primary-200 bg-primary-50 text-primary-700',
                slate: 'border-slate-200 bg-slate-50 text-slate-700',
                gray: 'border-gray-200 bg-gray-50 text-gray-700',
                red: 'border-red-200 bg-red-50 text-red-700',
                orange: 'border-orange-200 bg-orange-50 text-orange-700',
                amber: 'border-amber-200 bg-amber-50 text-amber-700',
                yellow: 'border-yellow-200 bg-yellow-50 text-yellow-700',
                lime: 'border-lime-200 bg-lime-50 text-lime-700',
                green: 'border-green-200 bg-green-50 text-green-700',
                emerald: 'border-green-200 bg-emerald-50 text-emerald-700',
                teal: 'border-teal-200 bg-teal-50 text-teal-700',
                cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700',
                sky: 'border-sky-200 bg-sky-50 text-sky-700',
                blue: 'border-blue-200 bg-blue-50 text-blue-700',
                indigo: 'border-indigo-200 bg-indigo-50 text-indigo-700',
                violet: 'border-violet-200 bg-violet-50 text-violet-700',
                purple: 'border-purple-200 bg-purple-50 text-purple-700',
                fuchsia: 'border-fuchsia-200 bg-fuchsia-50 text-fuchsia-700',
                pink: 'border-pink-200 bg-pink-50 text-pink-700',
                rose: 'border-rose-200 bg-rose-50 text-rose-700',
                white: 'bg-white text-gray-950 dark:text-gray-50',
            },
        },
        defaultVariants: {
            variant: 'default',
            theme: 'default',
        },
    },
);

function Badge({
    className,
    variant,
    theme,
    asChild = false,
    ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
    const Comp = asChild ? Slot : 'span';

    return <Comp data-slot="badge" className={cn(badgeVariants({ variant, theme }), className)} {...props} />;
}

export { Badge, badgeVariants };
